import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SelectField } from "@/components/ui/select";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme } from "@/contexts/ThemeContext";
import { apiService, SupportTicket, SupportMessage, SupportStats } from "@/services/api";
import { toast } from "sonner";
import dayjs from "dayjs";
import {
  LifeBuoy,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  MessageSquare,
  Eye,
  Send,
  User,
  Search,
  Filter,
  RefreshCw,
  ArrowLeft,
  Paperclip,
  MoreVertical,
} from "lucide-react";

export function HelpSupport() {
  const navigate = useNavigate();
  const { ticketId } = useParams();
  const { currentTheme } = useTheme();
  
  // State management
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [stats, setStats] = useState<SupportStats>({
    total: 0,
    open: 0,
    inProgress: 0,
    resolved: 0,
    closed: 0,
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load tickets
  const loadTickets = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (categoryFilter) params.category = categoryFilter;
      
      const response = await apiService.getAllSupportTickets(params);
      if (response.type === 0) {
        const data = response.data as any;
        setTickets(data.history || []);
      } else {
        toast.error(response.message || "Failed to load tickets");
      }
    } catch (error) {
      toast.error("Failed to load tickets");
      console.error("Error loading tickets:", error);
    } finally {
      setLoading(false);
    }
  };

  // Load stats
  const loadStats = async () => {
    try {
      const response = await apiService.getSupportStats();
      if (response.type === 0) {
        setStats(response.data);
      }
    } catch (error) {
      console.error("Error loading stats:", error);
    }
  };

  // Load ticket details and messages
  const loadTicketDetails = async (id: string) => {
    try {
      const [ticketResponse, messagesResponse] = await Promise.all([
        apiService.getSupportTicketById(id),
        apiService.getTicketMessages(id)
      ]);

      if (ticketResponse.type === 0) {
        setSelectedTicket(ticketResponse.data);
      }
      if (messagesResponse.type === 0) {
        setMessages(messagesResponse.data);
      }
    } catch (error) {
      toast.error("Failed to load ticket details");
      console.error("Error loading ticket details:", error);
    }
  };

  // Send message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedTicket) return;

    try {
      setIsSubmitting(true);
      const response = await apiService.addTicketMessage(selectedTicket._id, {
        message: newMessage,
        isInternal: false,
      });

      if (response.type === 0) {
        setNewMessage("");
        await loadTicketDetails(selectedTicket._id);
        toast.success("Message sent successfully");
      } else {
        toast.error(response.message || "Failed to send message");
      }
    } catch (error) {
      toast.error("Failed to send message");
      console.error("Error sending message:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Update ticket status
  const handleUpdateStatus = async (ticketId: string, status: string) => {
    try {
      const response = await apiService.updateSupportTicket(ticketId, { status });
      if (response.type === 0) {
        toast.success("Ticket updated successfully");
        await loadTickets();
        if (selectedTicket?._id === ticketId) {
          await loadTicketDetails(ticketId);
        }
      } else {
        toast.error(response.message || "Failed to update ticket");
      }
    } catch (error) {
      toast.error("Failed to update ticket");
      console.error("Error updating ticket:", error);
    }
  };

  useEffect(() => {
    void loadTickets();
    void loadStats();
  }, [statusFilter, priorityFilter, categoryFilter]);

  useEffect(() => {
    if (ticketId) {
      void loadTicketDetails(ticketId);
    }
  }, [ticketId]);

  // Filter tickets
  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = 
      ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.userId.fullName.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case "urgent":
        return <Badge className="bg-red-100 text-red-800">Urgent</Badge>;
      case "high":
        return <Badge className="bg-orange-100 text-orange-800">High</Badge>;
      case "medium":
        return <Badge className="bg-yellow-100 text-yellow-800">Medium</Badge>;
      case "low":
        return <Badge className="bg-green-100 text-green-800">Low</Badge>;
      default:
        return <Badge variant="outline">{priority}</Badge>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "open":
        return <Badge className="bg-blue-100 text-blue-800">Open</Badge>;
      case "in_progress":
        return <Badge className="bg-yellow-100 text-yellow-800">In Progress</Badge>;
      case "resolved":
        return <Badge className="bg-green-100 text-green-800">Resolved</Badge>;
      case "closed":
        return <Badge className="bg-gray-100 text-gray-800">Closed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getCategoryBadge = (category: string) => {
    switch (category) {
      case "technical":
        return <Badge variant="secondary" className="bg-purple-50 text-purple-700">Technical</Badge>;
      case "billing":
        return <Badge variant="secondary" className="bg-blue-50 text-blue-700">Billing</Badge>;
      case "account":
        return <Badge variant="secondary" className="bg-green-50 text-green-700">Account</Badge>;
      case "general":
        return <Badge variant="secondary" className="bg-gray-50 text-gray-700">General</Badge>;
      case "bug":
        return <Badge variant="secondary" className="bg-red-50 text-red-700">Bug</Badge>;
      case "feature":
        return <Badge variant="secondary" className="bg-orange-50 text-orange-700">Feature</Badge>;
      default:
        return <Badge variant="outline">{category}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format("MMM DD, YYYY HH:mm");
  };

  // If viewing a specific ticket, show the chat interface
  if (ticketId && selectedTicket) {
    return (
      <div className="h-screen flex flex-col">
        {/* Header */}
        <div className="border-b bg-white p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/help-support")}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-semibold">{selectedTicket.subject}</h1>
                <div className="flex items-center space-x-2 mt-1">
                  {getPriorityBadge(selectedTicket.priority)}
                  {getStatusBadge(selectedTicket.status)}
                  {getCategoryBadge(selectedTicket.category)}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <SelectField
                value={selectedTicket.status}
                onValueChange={(value: string) => handleUpdateStatus(selectedTicket._id, value)}
                placeholder="Update status"
                options={[
                  { label: "Open", value: "open" },
                  { label: "In Progress", value: "in_progress" },
                  { label: "Resolved", value: "resolved" },
                  { label: "Closed", value: "closed" },
                ]}
              />
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((message) => (
            <div
              key={message._id}
              className={`flex ${message.isInternal ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[70%] rounded-lg p-3 ${
                  message.isInternal
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted"
                }`}
              >
                <div className="flex items-center space-x-2 mb-1">
                  <User className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    {message.senderId.fullName}
                  </span>
                  <span className="text-xs opacity-70">
                    {formatDate(message.createdAt)}
                  </span>
                </div>
                <p className="text-sm">{message.message}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Message Input */}
        <div className="border-t bg-white p-4">
          <div className="flex items-center space-x-2">
            <Button variant="ghost" size="sm">
              <Paperclip className="w-4 h-4" />
            </Button>
            <Input
              placeholder="Type your message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void handleSendMessage();
                }
              }}
              className="flex-1"
            />
            <Button
              onClick={handleSendMessage}
              disabled={!newMessage.trim() || isSubmitting}
              style={{ backgroundColor: currentTheme.colors.primary }}
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Main tickets list view
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Help & Support</h1>
        <p className="text-muted-foreground">
          Manage user support tickets and provide assistance
        </p>
        </div>
        <Button
          onClick={() => {
            void loadTickets();
            void loadStats();
          }}
          variant="outline"
          size="sm"
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tickets</CardTitle>
            <LifeBuoy className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground">All support tickets</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Open</CardTitle>
            <AlertTriangle className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{stats.open}</div>
            <p className="text-xs text-muted-foreground">Awaiting response</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">In Progress</CardTitle>
            <Clock className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{stats.inProgress}</div>
            <p className="text-xs text-muted-foreground">Being worked on</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Resolved</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.resolved}</div>
            <p className="text-xs text-muted-foreground">Issue resolved</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Closed</CardTitle>
            <XCircle className="h-4 w-4 text-gray-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-600">{stats.closed}</div>
            <p className="text-xs text-muted-foreground">Tickets closed</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center space-x-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search tickets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <SelectField
              value={statusFilter}
              onValueChange={(value: string) => setStatusFilter(value)}
              placeholder="Filter by status"
              options={[
                { label: "All Status", value: "" },
                { label: "Open", value: "open" },
                { label: "In Progress", value: "in_progress" },
                { label: "Resolved", value: "resolved" },
                { label: "Closed", value: "closed" },
              ]}
            />
            <SelectField
              value={priorityFilter}
              onValueChange={(value: string) => setPriorityFilter(value)}
              placeholder="Filter by priority"
              options={[
                { label: "All Priority", value: "" },
                { label: "Low", value: "low" },
                { label: "Medium", value: "medium" },
                { label: "High", value: "high" },
                { label: "Urgent", value: "urgent" },
              ]}
            />
            <SelectField
              value={categoryFilter}
              onValueChange={(value: string) => setCategoryFilter(value)}
              placeholder="Filter by category"
              options={[
                { label: "All Categories", value: "" },
                { label: "Technical", value: "technical" },
                { label: "Billing", value: "billing" },
                { label: "Account", value: "account" },
                { label: "General", value: "general" },
                { label: "Bug", value: "bug" },
                { label: "Feature", value: "feature" },
              ]}
            />
          </div>
        </CardContent>
      </Card>

      {/* Tickets List */}
      <Card>
        <CardHeader>
          <CardTitle>Support Tickets</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin mr-2" />
              Loading tickets...
            </div>
          ) : filteredTickets.length === 0 ? (
            <div className="text-center py-8">
              <LifeBuoy className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No tickets found</p>
            </div>
          ) : (
          <div className="space-y-4">
              {filteredTickets.map((ticket) => (
                <div
                  key={ticket._id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/help-support/ticket/${ticket._id}`)}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-medium">{ticket.subject}</h3>
                    {getPriorityBadge(ticket.priority)}
                    {getStatusBadge(ticket.status)}
                    {getCategoryBadge(ticket.category)}
                  </div>
                    <p className="text-sm text-muted-foreground">
                      by {ticket.userId.fullName}
                    </p>
                  <p className="text-sm text-muted-foreground mt-1">
                      {formatDate(ticket.createdAt)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <MessageSquare className="h-4 w-4" />
                    <span>{ticket.messages.length}</span>
                  </div>
                  <Eye className="h-4 w-4 text-muted-foreground" />
                </div>
              </div>
            ))}
          </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
